import { useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  makeStyles,
  Button,
  Checkbox,
  Dropdown,
  Option,
  Field,
  FluentProvider,
  Input,
  InfoLabel,
  Label,
  Link,
  Spinner,
  webLightTheme,
} from '@fluentui/react-components';
import { OpenFilled } from '@fluentui/react-icons';
import { PersonAvailable24Regular } from '@fluentui/react-icons';
import { TinyColor } from '@ctrl/tinycolor';
import { useParams } from 'react-router-dom';
import { useBoolean } from '@fluentui/react-hooks';
import { FontIcon, Panel, PanelType } from '@fluentui/react';
import {
  createAssetStatus,
  getAssetStatuses,
  IAssetStatus,
  updateAssetStatus,
} from '../../services/assetServices';
import {
  createOrderStatusGroup,
  deleteOrderStatusGroup,
  getOrderStatusGroups,
  type IOrderStatusGroup,
  type StatusGroupKind,
  type OrderStatusGroupAudience,
  updateOrderStatusGroup,
} from '../../services/orderStatusGroupService';
import {
  createOrderStatus,
  getOrderStatuses,
  IStatusPermission,
  IOrderStatus,
  updateOrderStatus,
} from '../../services/workService';
import { getUserOrGroupsNames } from '../../services/userService';
import UserOrGroupSelector, {
  type IPermissionItem,
} from '../../common/selectors/UsersOrGroupSelectors';
import { ColorField } from '../../common/ColorField';
import { toHexOrEmpty } from '../../utils/color';
import { EditItem } from '@moko/ui';

type LabelRole = 'client' | 'manager' | 'contractor';

interface IOrderStatusGroupItem {
  id: number;
  statusId?: number;
  audience: OrderStatusGroupAudience;
  label: string;
  code?: string;
  color?: string;
  icon?: string;
  dashboardCardNumber: number | null;
}

const AUDIENCE_BY_ROLE: Record<LabelRole, OrderStatusGroupAudience> = {
  client: 'Client',
  manager: 'Manager',
  contractor: 'Contractor',
};

const ROLE_TITLE: Record<LabelRole, string> = {
  client: 'Client status group labels',
  manager: 'Manager status group labels',
  contractor: 'Contractor status group labels',
};

const STATUS_LABEL_FIELD: Record<
  LabelRole,
  'clientLabel' | 'managerLabel' | 'contractorLabel'
> = {
  client: 'clientLabel',
  manager: 'managerLabel',
  contractor: 'contractorLabel',
};

const STATUS_GROUP_ID_FIELD: Record<
  LabelRole,
  'clientStatusGroupId' | 'managerStatusGroupId' | 'contractorStatusGroupId'
> = {
  client: 'clientStatusGroupId',
  manager: 'managerStatusGroupId',
  contractor: 'contractorStatusGroupId',
};

const roleSupportsDashboardCard = (role: LabelRole): boolean =>
  role === 'client' || role === 'manager';

const useStyles = makeStyles({
  field: {
    width: '100%',
    maxWidth: '500px',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
  },
  actionLabelInput: {
    flex: 1,
    minWidth: 0,
  },
  colorDropdown: {
    minWidth: '150px',
  },
  lookupLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  roleDropdown: {
    flex: 1,
    minWidth: 0,
  },
  roleField: {
    display: 'grid',
    gridTemplateColumns: '175px minmax(0, 1fr)',
    alignItems: 'center',
    gap: '0px',
    width: '100%',
    maxWidth: '500px',
  },
  roleFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    width: '100%',
  },
  roleFieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '12px',
  },
  panelContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  panelTopRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelRowFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelSecondRowWithDashboard: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelSecondRow: {
    display: 'grid',
    gridTemplateColumns: '1.8fr 1fr',
    gap: '8px',
    alignItems: 'start',
  },
  panelHeaderActions: {
    display: 'flex',
    gap: '6px',
  },
  panelListHeader: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 92px',
    gap: '8px',
    padding: '6px 8px',
    borderBottom: '1px solid #edebe9',
    fontWeight: 600,
  },
  panelListHeaderWithDashboard: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 72px 92px',
    gap: '8px',
    padding: '6px 8px',
    borderBottom: '1px solid #edebe9',
    fontWeight: 600,
  },
  panelListRow: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 92px',
    gap: '8px',
    padding: '8px',
    borderBottom: '1px solid #f3f2f1',
    alignItems: 'center',
  },
  panelListRowWithDashboard: {
    display: 'grid',
    gridTemplateColumns: '3.4fr 2fr 56px 56px 72px 92px',
    gap: '8px',
    padding: '8px',
    borderBottom: '1px solid #f3f2f1',
    alignItems: 'center',
  },
  panelListCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  panelActionsHeader: {
    textAlign: 'right',
  },
  panelCheckbox: {
    marginBottom: '6px',
  },
  panelActions: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'flex-end',
  },
  panelPreviewColor: {
    margin: '0',
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
    },
  },
  panelListIcon: {
    width: '16px',
    height: '16px',
    fontSize: '16px',
    lineHeight: '16px',
  },
  panelActionIconButton: {
    minWidth: '32px',
    padding: '0',
  },
});

const Status = () => {
  const isSaved = useRef(false);
  const { type } = useParams();

  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [icon, setIcon] = useState<string>();
  const [colorHex, setColorHex] = useState<string>('#ffffff');
  const [accesses, setAccesses] = useState<IPermissionItem[]>([]);
  const [statusClientLabel, setStatusClientLabel] = useState<string>();
  const [statusManagerLabel, setStatusManagerLabel] = useState<string>();
  const [statusContractorLabel, setStatusContractorLabel] = useState<string>();
  const [statusClientCode, setStatusClientCode] = useState<string>();
  const [statusManagerCode, setStatusManagerCode] = useState<string>();
  const [statusContractorCode, setStatusContractorCode] = useState<string>();
  const [statusClientGroupId, setStatusClientGroupId] = useState<number | null>(
    null
  );
  const [statusManagerGroupId, setStatusManagerGroupId] = useState<
    number | null
  >(null);
  const [statusContractorGroupId, setStatusContractorGroupId] = useState<
    number | null
  >(null);
  const [orderStatusGroupItemsByRole, setOrderStatusGroupItemsByRole] =
    useState<Record<LabelRole, IOrderStatusGroupItem[]>>({
      client: [],
      manager: [],
      contractor: [],
    });
  const [isLabelsLoading, setIsLabelsLoading] = useState(false);
  const [isLabelMutationInProgress, setIsLabelMutationInProgress] =
    useState(false);
  const [activeLabelPanelRole, setActiveLabelPanelRole] =
    useState<LabelRole | null>(null);
  const [newPanelLabel, setNewPanelLabel] = useState('');
  const [newPanelCode, setNewPanelCode] = useState('');
  const [newPanelColor, setNewPanelColor] = useState('');
  const [newPanelIcon, setNewPanelIcon] = useState('');
  const [newPanelDashboardCardNumber, setNewPanelDashboardCardNumber] =
    useState<string>('');
  const [activeStatusId, setActiveStatusId] = useState<number | null>(null);
  const [editingOrderStatusGroupId, setEditingOrderStatusGroupId] = useState<
    number | null
  >(null);
  const [editingStatusId, setEditingStatusId] = useState<number | null>(null);
  const [
    isAccessSelectorOpen,
    { setTrue: showAccessSelector, setFalse: hideAccessSelector },
  ] = useBoolean(false);
  const styles = useStyles();

  // Order statuses support per-status access permissions.
  const supportsPermissions = type === 'order';
  const supportsContractorLabel = false;
  const statusGroupKind: StatusGroupKind = 'order';

  const statusFields =
    type === 'order'
      ? [
          { name: 'name', fieldType: 'String', label: 'Name' },
          { name: 'code', fieldType: 'String', label: 'Code' },
          {
            name: 'isCompleted',
            fieldType: 'Checkbox',
            label: 'Is Completed',
          },
          { name: 'lockEntity', fieldType: 'Checkbox', label: 'Lock Entity' },
        ]
      : [
          { name: 'name', fieldType: 'String', label: 'Name' },
          { name: 'code', fieldType: 'String', label: 'Code' },
          { name: 'lockEntity', fieldType: 'Checkbox', label: 'Lock Entity' },
        ];

  const toBoolean = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  };

  const toRequiredString = (value: unknown): string =>
    typeof value === 'string' ? value : '';

  const toOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
      return undefined;
    }
    return value;
  };

  const toDashboardCardNumber = (value: unknown): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'boolean') {
      return value ? 1 : null;
    }
    return null;
  };

  const toLegacyDashboardFlag = (value: number | null): boolean =>
    value != null;

  const buildStatusGroupPayload = (
    role: LabelRole,
    label: string,
    statusGroupKind: StatusGroupKind,
    statusId: number
  ) => {
    const dashboardCardNumber = roleSupportsDashboardCard(role)
      ? toDashboardCardNumber(newPanelDashboardCardNumber)
      : null;

    const payload: {
      statusId: number;
      audience: OrderStatusGroupAudience;
      name: string;
      code?: string;
      color?: string;
      icon?: string;
      showInDashboardCard?: boolean | number | null;
      dashboardCardNumber?: number | null;
    } = {
      statusId,
      audience: AUDIENCE_BY_ROLE[role],
      name: label,
      code: toOptionalString(newPanelCode),
      color: toOptionalString(newPanelColor),
      icon: toOptionalString(newPanelIcon),
      showInDashboardCard: toLegacyDashboardFlag(dashboardCardNumber),
    };

    // Order status-group API currently rejects null/unknown numeric field.

    return payload;
  };

  const findGroupBySelection = (
    role: LabelRole,
    selection?: string
  ): IOrderStatusGroupItem | undefined => {
    if (!selection) return undefined;
    return orderStatusGroupItemsByRole[role].find(
      (item) => item.label === selection || item.code === selection
    );
  };

  const resolveSelectionValue = (
    role: LabelRole,
    statusValue?: string
  ): { label?: string; code?: string } => {
    if (!statusValue) {
      return { label: undefined, code: undefined };
    }

    const item = orderStatusGroupItemsByRole[role].find(
      (x) => x.code === statusValue || x.label === statusValue
    );

    if (!item) {
      return { label: statusValue, code: statusValue };
    }

    return {
      label: item.label,
      code: item.code ?? statusValue,
    };
  };

  const dropdownItemsFor = (
    role: LabelRole,
    selectedLabel?: string
  ): IOrderStatusGroupItem[] => {
    const items = orderStatusGroupItemsByRole[role];
    if (!selectedLabel) return items;
    if (
      items.some(
        (item) => item.label === selectedLabel || item.code === selectedLabel
      )
    ) {
      return items;
    }
    return [
      ...items,
      {
        id: -1,
        statusId: undefined,
        audience: AUDIENCE_BY_ROLE[role],
        label: selectedLabel,
        code: undefined,
        color: undefined,
        icon: undefined,
        dashboardCardNumber: null,
      },
    ];
  };

  const resolveDropdownDisplayValue = (
    role: LabelRole,
    labelValue?: string,
    codeValue?: string
  ): string => {
    const byCode = codeValue
      ? findGroupBySelection(role, codeValue)
      : undefined;
    if (byCode?.label) return byCode.label;

    const byLabel = labelValue
      ? findGroupBySelection(role, labelValue)
      : undefined;
    if (byLabel?.label) return byLabel.label;

    return labelValue ?? codeValue ?? '';
  };

  const readStatusLabelValue = (
    status: IOrderStatus,
    role: LabelRole
  ): string | undefined => status[STATUS_LABEL_FIELD[role]];

  const readStatusGroupId = (
    status: IOrderStatus,
    role: LabelRole
  ): number | null => status[STATUS_GROUP_ID_FIELD[role]] ?? null;

  const loadRoleLabels = async (role: LabelRole) => {
    const abortController = new AbortController();
    const response = await getOrderStatusGroups(
      abortController,
      AUDIENCE_BY_ROLE[role],
      statusGroupKind
    );
    const nextItems: IOrderStatusGroupItem[] = (response ?? [])
      .filter((item: IOrderStatusGroup) => !item.isDeleted)
      .map((item: IOrderStatusGroup) => ({
        id: item.id,
        statusId: item.statusId,
        audience: item.audience,
        label: item.name,
        code: item.code,
        color: item.color,
        icon: item.icon,
        dashboardCardNumber: toDashboardCardNumber(
          item.dashboardCardNumber ?? item.showInDashboardCard
        ),
      }));

    setOrderStatusGroupItemsByRole((prev) => ({
      ...prev,
      [role]: nextItems,
    }));
  };

  const loadAllRoleLabels = async () => {
    if (!supportsPermissions) return;
    setIsLabelsLoading(true);
    try {
      const tasks: Promise<void>[] = [
        loadRoleLabels('client'),
        loadRoleLabels('manager'),
      ];
      if (supportsContractorLabel) {
        tasks.push(loadRoleLabels('contractor'));
      }
      await Promise.all(tasks);
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to load order status groups.');
    } finally {
      setIsLabelsLoading(false);
    }
  };

  const resetPanelTemplate = () => {
    setNewPanelLabel('');
    setNewPanelCode('');
    setNewPanelColor('');
    setNewPanelIcon('');
    setNewPanelDashboardCardNumber('');
    setEditingOrderStatusGroupId(null);
    setEditingStatusId(null);
  };

  const openLabelPanel = (role: LabelRole) => {
    if (activeStatusId == null) {
      setErrorMessage('Save the status before managing its labels.');
      return;
    }
    setActiveLabelPanelRole(role);
    resetPanelTemplate();
  };

  const closeLabelPanel = () => {
    setActiveLabelPanelRole(null);
    resetPanelTemplate();
  };

  const startTemplateEdit = (item: IOrderStatusGroupItem) => {
    setEditingOrderStatusGroupId(item.id);
    setEditingStatusId(item.statusId ?? null);
    setNewPanelLabel(item.label ?? '');
    setNewPanelCode(item.code ?? '');
    setNewPanelColor(new TinyColor(item.color || '#ffffff').toHexString());
    setNewPanelIcon(item.icon ?? '');
    setNewPanelDashboardCardNumber(
      item.dashboardCardNumber != null ? String(item.dashboardCardNumber) : ''
    );
  };

  const cancelTemplateEdit = () => {
    resetPanelTemplate();
  };

  const addLabelItem = async (role: LabelRole) => {
    const label = newPanelLabel.trim();
    if (!label) return;
    if (activeStatusId == null) {
      setErrorMessage('Save the status before managing its labels.');
      return;
    }
    setIsLabelMutationInProgress(true);
    const abortController = new AbortController();
    try {
      await createOrderStatusGroup(
        abortController,
        buildStatusGroupPayload(role, label, statusGroupKind, activeStatusId),
        statusGroupKind
      );
      setSuccessMessage('Label added.');
      setNewPanelLabel('');
      setNewPanelCode('');
      setNewPanelColor('');
      setNewPanelIcon('');
      setNewPanelDashboardCardNumber('');
      await loadRoleLabels(role);
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to create label.');
    } finally {
      setIsLabelMutationInProgress(false);
    }
  };

  const saveEditedOrderStatusGroup = async (role: LabelRole) => {
    if (editingOrderStatusGroupId == null) return false;

    const trimmedLabel = newPanelLabel.trim();
    if (!trimmedLabel) {
      setErrorMessage('Label value is required.');
      return false;
    }

    const statusId = editingStatusId ?? activeStatusId;
    if (statusId == null) {
      setErrorMessage('A parent status is required to update this label.');
      return false;
    }

    setIsLabelMutationInProgress(true);
    const abortController = new AbortController();
    try {
      await updateOrderStatusGroup(
        abortController,
        editingOrderStatusGroupId,
        {
          ...buildStatusGroupPayload(
            role,
            trimmedLabel,
            statusGroupKind,
            statusId
          ),
          isDeleted: false,
        },
        statusGroupKind
      );
      setSuccessMessage('Label updated.');
      await loadRoleLabels(role);
      resetPanelTemplate();
      return true;
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to update label.');
      return false;
    } finally {
      setIsLabelMutationInProgress(false);
    }
  };

  const removeLabelItem = async (role: LabelRole, itemId: number) => {
    setIsLabelMutationInProgress(true);
    const abortController = new AbortController();
    try {
      await deleteOrderStatusGroup(abortController, itemId, statusGroupKind);
      setSuccessMessage('Label deleted.');
      await loadRoleLabels(role);
    } catch (error: any) {
      setErrorMessage(error?.message ?? 'Failed to delete label.');
    } finally {
      setIsLabelMutationInProgress(false);
    }
  };

  const loadAccessesFromPermissions = async (
    permissions: IStatusPermission[],
    abortController: AbortController
  ) => {
    const items: IPermissionItem[] = permissions
      .map((p) => {
        if (p.userId != null) return { id: p.userId, isGroup: false };
        if (p.groupId != null) return { id: p.groupId, isGroup: true };
        return null;
      })
      .filter((x): x is IPermissionItem => x !== null);

    if (items.length === 0) {
      setAccesses([]);
      return;
    }

    const keyFor = (i: IPermissionItem) => `${i.isGroup ? 'G' : 'U'}${i.id}`;

    try {
      const ids = items.map(keyFor);
      const names: Record<string, string> | undefined =
        await getUserOrGroupsNames(abortController, ids);
      const lookup = new Map(Object.entries(names ?? {}));
      setAccesses(
        items.map((i) => {
          const resolvedName = lookup.get(keyFor(i));
          return {
            ...i,
            name: resolvedName,
            username: i.isGroup ? undefined : resolvedName,
          };
        })
      );
    } catch {
      setAccesses(items);
    }
  };

  /** Convert UI state back into the API permission shape. */
  const buildPermissionsPayload = (): IStatusPermission[] =>
    accesses.map((a) => ({
      groupId: a.isGroup ? a.id : null,
      userId: a.isGroup ? null : a.id,
      tenantId: null,
    }));

  useEffect(() => {
    if (!supportsPermissions) return;
    if (orderStatusGroupItemsByRole.client.length > 0) return;
    void loadAllRoleLabels();
  }, [supportsPermissions]);

  return (
    <>
      <div>{type === 'order' ? 'Order' : 'Asset'} Status</div>
      <EditItem
        getAction={async (abortController, id) => {
          setActiveStatusId(id);
          const applyColorState = (status: IOrderStatus | IAssetStatus) => {
            setIcon(status.icon);
            setColorHex(new TinyColor(status.color || '#ffffff').toHexString());
          };

          if (type === 'order') {
            const data = await getOrderStatuses(abortController);
            const status = data.find((s) => s.id === id);
            if (!status) return status;
            applyColorState(status);
            await loadAccessesFromPermissions(
              status.permissions ?? [],
              abortController
            );
            const clientSelection = resolveSelectionValue(
              'client',
              readStatusLabelValue(status, 'client')
            );
            const managerSelection = resolveSelectionValue(
              'manager',
              readStatusLabelValue(status, 'manager')
            );
            const contractorSelection = resolveSelectionValue(
              'contractor',
              readStatusLabelValue(status, 'contractor')
            );

            setStatusClientLabel(clientSelection.label);
            setStatusClientCode(clientSelection.code);
            setStatusClientGroupId(readStatusGroupId(status, 'client'));
            setStatusManagerLabel(managerSelection.label);
            setStatusManagerCode(managerSelection.code);
            setStatusManagerGroupId(readStatusGroupId(status, 'manager'));
            setStatusContractorLabel(
              supportsContractorLabel ? contractorSelection.label : undefined
            );
            setStatusContractorCode(
              supportsContractorLabel ? contractorSelection.code : undefined
            );
            setStatusContractorGroupId(
              supportsContractorLabel
                ? readStatusGroupId(status, 'contractor')
                : null
            );
            return {
              ...status,
              isCompleted: status.isCompleted ?? status.IsCompleted,
              lockEntity: status.lockEntity ?? status.LockEntity,
            };
          } else {
            const data: IAssetStatus[] = await getAssetStatuses(
              abortController
            );
            const status = data.find((s) => s.id === id);
            if (status) applyColorState(status);
            return status
              ? {
                  ...status,
                  lockEntity: status.lockEntity ?? status.LockEntity,
                }
              : status;
          }
        }}
        newAction={async (abortController, newStatus) => {
          if (type === 'asset') {
            const newStatusResponse = await createAssetStatus(abortController, {
              name: toRequiredString(newStatus.name),
              code: toOptionalString(newStatus.code),
              color: colorHex,
              icon: icon,
              lockEntity: toBoolean(newStatus.lockEntity),
            });
            setSuccessMessage(
              `Status with id: ${newStatusResponse.assetStatusId} created.`
            );
          } else {
            const createFn = createOrderStatus;
            const newStatusResponse = await createFn(abortController, {
              name: toRequiredString(newStatus.name),
              code: toOptionalString(newStatus.code),
              color: colorHex,
              icon: icon,
              isCompleted: toBoolean(newStatus.isCompleted),
              lockEntity: toBoolean(newStatus.lockEntity),
              clientStatusGroupId: statusClientGroupId,
              managerStatusGroupId: statusManagerGroupId,
              contractorStatusGroupId: supportsContractorLabel
                ? statusContractorGroupId
                : undefined,
              permissions: buildPermissionsPayload(),
            });
            setSuccessMessage(
              `Status with id: ${newStatusResponse.assetStatusId} created.`
            );
          }
        }}
        editAction={async (abortController, id, editedStatus) => {
          if (type === 'asset') {
            await updateAssetStatus(abortController, id, {
              name: toRequiredString(editedStatus.name),
              code: toOptionalString(editedStatus.code),
              color: colorHex,
              icon: icon,
              lockEntity: toBoolean(editedStatus.lockEntity),
            });
            setSuccessMessage(`Status with id: ${id} successfully updated.`);
          } else {
            const updateFn = updateOrderStatus;
            await updateFn(abortController, id, {
              name: toRequiredString(editedStatus.name),
              code: toOptionalString(editedStatus.code),
              color: colorHex,
              icon: icon,
              isCompleted: toBoolean(editedStatus.isCompleted),
              lockEntity: toBoolean(editedStatus.lockEntity),
              clientStatusGroupId: statusClientGroupId,
              managerStatusGroupId: statusManagerGroupId,
              contractorStatusGroupId: supportsContractorLabel
                ? statusContractorGroupId
                : undefined,
              permissions: buildPermissionsPayload(),
            });
            setSuccessMessage(`Status with id: ${id} successfully updated.`);
          }
        }}
        isSaved={isSaved}
        back={`/settings/${type}Statuses`}
        preButtons={
          supportsPermissions
            ? [
                {
                  text: 'Access',
                  icon: <PersonAvailable24Regular />,
                  onClick: () => showAccessSelector(),
                },
              ]
            : undefined
        }
        metadata={{
          fields: statusFields,
          lookups: [],
          validations: [],
          interactions: [],
        }}
      >
        {supportsPermissions && (
          <div className={styles.roleFieldsContainer}>
            <Field
              label="Client Label"
              orientation="horizontal"
              className={styles.roleField}
            >
              <div className={styles.roleFieldRow}>
                <Dropdown
                  className={styles.roleDropdown}
                  placeholder="Select client label"
                  value={resolveDropdownDisplayValue(
                    'client',
                    statusClientLabel,
                    statusClientCode
                  )}
                  selectedOptions={
                    resolveDropdownDisplayValue(
                      'client',
                      statusClientLabel,
                      statusClientCode
                    )
                      ? [
                          resolveDropdownDisplayValue(
                            'client',
                            statusClientLabel,
                            statusClientCode
                          ),
                        ]
                      : []
                  }
                  onOptionSelect={(_e, data) => {
                    const selectedLabel = data.optionValue;
                    setStatusClientLabel(selectedLabel);
                    const selectedItem = findGroupBySelection(
                      'client',
                      selectedLabel
                    );
                    setStatusClientCode(selectedItem?.code ?? selectedLabel);
                    setStatusClientGroupId(selectedItem?.id ?? null);
                  }}
                >
                  {dropdownItemsFor('client', statusClientLabel).map((item) => (
                    <Option key={`${item.id}-${item.label}`} value={item.label}>
                      {item.label}
                    </Option>
                  ))}
                </Dropdown>
                <Link onClick={() => openLabelPanel('client')}>manage</Link>
              </div>
            </Field>

            <Field
              label="Manager Label"
              orientation="horizontal"
              className={styles.roleField}
            >
              <div className={styles.roleFieldRow}>
                <Dropdown
                  className={styles.roleDropdown}
                  placeholder="Select manager label"
                  value={resolveDropdownDisplayValue(
                    'manager',
                    statusManagerLabel,
                    statusManagerCode
                  )}
                  selectedOptions={
                    resolveDropdownDisplayValue(
                      'manager',
                      statusManagerLabel,
                      statusManagerCode
                    )
                      ? [
                          resolveDropdownDisplayValue(
                            'manager',
                            statusManagerLabel,
                            statusManagerCode
                          ),
                        ]
                      : []
                  }
                  onOptionSelect={(_e, data) => {
                    const selectedLabel = data.optionValue;
                    setStatusManagerLabel(selectedLabel);
                    const selectedItem = findGroupBySelection(
                      'manager',
                      selectedLabel
                    );
                    setStatusManagerCode(selectedItem?.code ?? selectedLabel);
                    setStatusManagerGroupId(selectedItem?.id ?? null);
                  }}
                >
                  {dropdownItemsFor('manager', statusManagerLabel).map(
                    (item) => (
                      <Option
                        key={`${item.id}-${item.label}`}
                        value={item.label}
                      >
                        {item.label}
                      </Option>
                    )
                  )}
                </Dropdown>
                <Link onClick={() => openLabelPanel('manager')}>manage</Link>
              </div>
            </Field>

            {supportsContractorLabel && (
              <Field
                label="Contractor Label"
                orientation="horizontal"
                className={styles.roleField}
              >
                <div className={styles.roleFieldRow}>
                  <Dropdown
                    className={styles.roleDropdown}
                    placeholder="Select contractor label"
                    value={resolveDropdownDisplayValue(
                      'contractor',
                      statusContractorLabel,
                      statusContractorCode
                    )}
                    selectedOptions={
                      resolveDropdownDisplayValue(
                        'contractor',
                        statusContractorLabel,
                        statusContractorCode
                      )
                        ? [
                            resolveDropdownDisplayValue(
                              'contractor',
                              statusContractorLabel,
                              statusContractorCode
                            ),
                          ]
                        : []
                    }
                    onOptionSelect={(_e, data) => {
                      const selectedLabel = data.optionValue;
                      setStatusContractorLabel(selectedLabel);
                      const selectedItem = findGroupBySelection(
                        'contractor',
                        selectedLabel
                      );
                      setStatusContractorCode(
                        selectedItem?.code ?? selectedLabel
                      );
                      setStatusContractorGroupId(selectedItem?.id ?? null);
                    }}
                  >
                    {dropdownItemsFor('contractor', statusContractorLabel).map(
                      (item) => (
                        <Option
                          key={`${item.id}-${item.label}`}
                          value={item.label}
                        >
                          {item.label}
                        </Option>
                      )
                    )}
                  </Dropdown>
                  <Link onClick={() => openLabelPanel('contractor')}>
                    manage
                  </Link>
                </div>
              </Field>
            )}
          </div>
        )}

        <Field
          label="Icon"
          orientation="horizontal"
          className={styles.field}
          hint={
            <div>
              <InfoLabel>Use an icon name from flicon.io</InfoLabel>
              <Link href="https://flicon.io" target="_blank">
                <OpenFilled />
              </Link>
            </div>
          }
        >
          <Input
            value={icon ?? ''}
            onChange={(_, newValue) => setIcon(newValue.value)}
          />
        </Field>
        <Field label="Color" orientation="horizontal" className={styles.field}>
          <ColorField value={colorHex} onChange={setColorHex} />
        </Field>
      </EditItem>
      {supportsPermissions && (
        <UserOrGroupSelector
          isOpen={isAccessSelectorOpen}
          hideUsersAndGroupsSelector={hideAccessSelector}
          permissions={accesses}
          onUserOrGroupSelected={(id, isGroup, username, name) => {
            if (accesses.some((a) => a.id === id && a.isGroup === isGroup)) {
              return;
            }
            // The DetailsList "User|Group Name" column reads `name`, so for
            // users we copy `username` into `name` to render the row right
            // after selection (same trick as Tenant.tsx, Bug 391).
            setAccesses([
              ...accesses,
              { id, isGroup, username, name: name ?? username },
            ]);
          }}
          onRemovePermission={(id) =>
            setAccesses(accesses.filter((a) => a.id !== id))
          }
        />
      )}

      <Panel
        isOpen={activeLabelPanelRole != null}
        onDismiss={closeLabelPanel}
        type={PanelType.medium}
        headerText={
          activeLabelPanelRole != null
            ? `Manage ${ROLE_TITLE[activeLabelPanelRole]}`
            : 'Manage labels'
        }
      >
        {activeLabelPanelRole != null && (
          <FluentProvider theme={webLightTheme}>
            <div className={styles.panelContainer}>
              <div className={styles.panelTopRow}>
                <Field label="New label">
                  <Input
                    value={newPanelLabel}
                    onChange={(_, data) => setNewPanelLabel(data.value)}
                  />
                </Field>
                <Field label="Code">
                  <Input
                    value={newPanelCode}
                    onChange={(_, data) => setNewPanelCode(data.value)}
                  />
                </Field>
              </div>

              <div
                className={
                  roleSupportsDashboardCard(activeLabelPanelRole)
                    ? styles.panelSecondRowWithDashboard
                    : styles.panelSecondRow
                }
              >
                <Field label="Color">
                  <ColorField
                    value={toHexOrEmpty(newPanelColor)}
                    onChange={setNewPanelColor}
                  />
                </Field>
                <Field
                  label="Icon"
                  hint={
                    <div>
                      <InfoLabel>Use an icon name from flicon.io</InfoLabel>
                      <Link href="https://flicon.io" target="_blank">
                        <OpenFilled />
                      </Link>
                    </div>
                  }
                >
                  <Input
                    value={newPanelIcon}
                    onChange={(_, data) => setNewPanelIcon(data.value)}
                  />
                </Field>

                {roleSupportsDashboardCard(activeLabelPanelRole) && (
                  <Field label="Dashboard card">
                    <Input
                      type="number"
                      value={newPanelDashboardCardNumber}
                      onChange={(_, data) =>
                        setNewPanelDashboardCardNumber(data.value)
                      }
                      placeholder="Card number"
                    />
                  </Field>
                )}
              </div>

              <div className={styles.panelHeaderActions}>
                {editingOrderStatusGroupId == null ? (
                  <Button
                    appearance="primary"
                    disabled={
                      isLabelMutationInProgress ||
                      newPanelLabel.trim().length === 0
                    }
                    onClick={() => {
                      void addLabelItem(activeLabelPanelRole);
                    }}
                  >
                    Add
                  </Button>
                ) : (
                  <>
                    <Button
                      appearance="primary"
                      disabled={
                        isLabelMutationInProgress ||
                        newPanelLabel.trim().length === 0
                      }
                      onClick={async () => {
                        await saveEditedOrderStatusGroup(activeLabelPanelRole);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      appearance="secondary"
                      disabled={isLabelMutationInProgress}
                      onClick={() => {
                        cancelTemplateEdit();
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>

              {isLabelsLoading ? (
                <Spinner label="Loading labels..." />
              ) : (
                <>
                  <div
                    className={
                      roleSupportsDashboardCard(activeLabelPanelRole)
                        ? styles.panelListHeaderWithDashboard
                        : styles.panelListHeader
                    }
                  >
                    <span>Label</span>
                    <span>Code</span>
                    <span>Icon</span>
                    <span>Color</span>
                    {roleSupportsDashboardCard(activeLabelPanelRole) && (
                      <span>Dashboard</span>
                    )}
                    <span className={styles.panelActionsHeader}>Actions</span>
                  </div>

                  {orderStatusGroupItemsByRole[activeLabelPanelRole].map(
                    (item) => (
                      <div
                        key={item.id}
                        className={
                          roleSupportsDashboardCard(activeLabelPanelRole)
                            ? styles.panelListRowWithDashboard
                            : styles.panelListRow
                        }
                      >
                        <span className={styles.panelListCell}>
                          {item.label}
                        </span>
                        <span className={styles.panelListCell}>
                          {item.code ?? ''}
                        </span>
                        <span className={styles.panelListCell}>
                          {item.icon ? (
                            <FontIcon
                              iconName={item.icon}
                              className={styles.panelListIcon}
                            />
                          ) : (
                            ''
                          )}
                        </span>
                        <div
                          className={styles.panelPreviewColor}
                          title={item.color ?? ''}
                          style={{
                            backgroundColor:
                              toHexOrEmpty(item.color ?? '') || '#ffffff',
                          }}
                        />
                        {roleSupportsDashboardCard(activeLabelPanelRole) && (
                          <span className={styles.panelListCell}>
                            {item.dashboardCardNumber != null
                              ? String(item.dashboardCardNumber)
                              : ''}
                          </span>
                        )}
                        <div className={styles.panelActions}>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<FontIcon iconName="Edit" />}
                            aria-label="Edit"
                            title="Edit"
                            className={styles.panelActionIconButton}
                            disabled={
                              isLabelMutationInProgress ||
                              editingOrderStatusGroupId != null
                            }
                            onClick={() => {
                              startTemplateEdit(item);
                            }}
                          />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<FontIcon iconName="Delete" />}
                            aria-label="Delete"
                            title="Delete"
                            className={styles.panelActionIconButton}
                            disabled={
                              isLabelMutationInProgress ||
                              editingOrderStatusGroupId != null
                            }
                            onClick={() => {
                              void removeLabelItem(
                                activeLabelPanelRole,
                                item.id
                              );
                            }}
                          />
                        </div>
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </FluentProvider>
        )}
      </Panel>
    </>
  );
};

export default Status;
